/*
  Guldnatt – designlager (tastelyft 25 okt 2026).
  1. Sektioner som kommer in i bild avslöjas en gång (innehållet syns om skriptet aldrig körs).
  2. Videor ([data-hs-video]): källorna (HLS först, annars mp4) sätts först nära skärmen, filmen spelar ljudlöst bara när den syns,
     pausas utanför bild och när fliken göms, och har en Pausa/Spela-knapp. Vid "reducera rörelse"
     eller datasparläge spelar ingen video av sig själv.
  Inget körs i temaredigeraren.
*/
(() => {
  const root = document.documentElement;
  if (root.classList.contains('shopify-design-mode')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hasObserver = 'IntersectionObserver' in window;
  root.classList.add('hs-js');

  /* ---- Avslöjanden ---- */
  const revealTargets = document.querySelectorAll('[data-hs-reveal], [data-hs-stagger]');
  if (!hasObserver || reduceMotion.matches) {
    revealTargets.forEach((element) => element.classList.add('is-in'));
  } else {
    const revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          revealObserver.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );
    revealTargets.forEach((element) => revealObserver.observe(element));
  }

  /* ---- Videor ---- */
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);

  /* En rörlig bild per skärm: i en grupp ([data-hs-film-grupp]) spelar bara en film åt gången */
  const visibleVideos = new WeakSet();
  const groupOf = (video) => video.closest('[data-hs-film-grupp]');
  const othersPlaying = (video) => {
    const group = groupOf(video);
    if (!group) return false;
    return [...group.querySelectorAll('video')].some((other) => other !== video && !other.paused && visibleVideos.has(other));
  };
  const pauseOthers = (video) => {
    const group = groupOf(video);
    if (!group) return;
    group.querySelectorAll('video').forEach((other) => {
      if (other !== video && !other.paused) other.pause();
    });
  };

  const setupVideo = (video) => {
    if (video.dataset.hsReady) return;
    video.dataset.hsReady = 'true';

    const toggle = video.parentElement?.querySelector('[data-hs-video-toggle]');
    const threshold = Number(video.dataset.hsThreshold || 0.35);
    let wantsPlay = !reduceMotion.matches && !saveData;
    let visible = false;

    // Webbläsaren väljer själv första källan den kan spela (HLS anpassar upplösningen efter uppkopplingen)
    const ensureSource = () => {
      if (video.dataset.hsLoaded) return;
      const sources = video.querySelectorAll('source[data-src]');
      if (!sources.length) return;
      video.dataset.hsLoaded = 'true';
      sources.forEach((source) => source.setAttribute('src', source.dataset.src));
      video.preload = 'metadata';
      video.load();
    };

    const syncLabel = () => {
      if (!toggle) return;
      const playing = !video.paused;
      toggle.textContent = playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay;
      toggle.classList.toggle('is-playing', playing);
    };

    const play = () => {
      ensureSource();
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => syncLabel());
    };

    video.addEventListener('play', () => {
      pauseOthers(video);
      syncLabel();
    });
    video.addEventListener('pause', syncLabel);

    toggle?.addEventListener('click', () => {
      if (video.paused) {
        wantsPlay = true;
        play();
      } else {
        wantsPlay = false;
        video.pause();
      }
    });

    if (hasObserver) {
      const nearObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting) && wantsPlay) {
            ensureSource();
            nearObserver.disconnect();
          }
        },
        { rootMargin: '300px 0px' }
      );
      nearObserver.observe(video);

      const playObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            visible = entry.isIntersecting && entry.intersectionRatio >= threshold;
            if (visible) visibleVideos.add(video);
            else visibleVideos.delete(video);
            if (visible && wantsPlay && !othersPlaying(video)) play();
            if (!visible && !video.paused) {
              video.pause();
              groupOf(video)?.dispatchEvent(new CustomEvent('hs-film-ledig'));
            }
          }
        },
        { threshold: [0, threshold] }
      );
      playObserver.observe(video);

      // När en film i gruppen lämnar bild får nästa synliga ta över
      groupOf(video)?.addEventListener('hs-film-ledig', () => {
        if (visible && wantsPlay && video.paused && !othersPlaying(video)) play();
      });
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden && !video.paused) video.pause();
      if (!document.hidden && visible && wantsPlay) play();
    });

    syncLabel();
  };

  document.querySelectorAll('[data-hs-video]').forEach(setupVideo);
})();
